import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './HostDetailModal.css';
import { getUserProfile } from '../utils/firestoreUser';
import PreviewProfileModal from './PreviewProfileModal';
import ReactCountryFlag from 'react-country-flag';
import { countryToAlpha2 } from 'country-to-iso';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Using a more robust Flag component that can be shared or expanded
const Flag = ({ code }) => {
  const flags = {
    FR: '🇫🇷', DE: '🇩🇪', IT: '🇮🇹', US: '🇺🇸',
    // Add more country codes and their flags here
  };
  return flags[code] ? <span className="host-flag-emoji">{flags[code]}</span> : null;
};

function HostDetailModal({ host, onClose, onOpenRequest, isRequestSent, onImageClick, onProfileClick, isDanielAndSarah, dinners, currentIndex, onNavigate }) {
  const navigate = useNavigate();
  const [hostProfile, setHostProfile] = useState(null);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [showEnlargedImage, setShowEnlargedImage] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    async function fetchHostProfile() {
      if (host.hostId) {
        setIsProfileLoading(true);
        const profile = await getUserProfile(host.hostId);
        setHostProfile(profile);
        setIsProfileLoading(false);
      } else {
        setIsProfileLoading(false);
      }
    }
    fetchHostProfile();
  }, [host.hostId]);

  useEffect(() => {
    async function fetchReviews() {
      if (!host.hostId) return;
      const fetchedReviews = [];
      const userCache = {};
      const getUserData = async (uid) => {
        if (!uid) return null;
        if (userCache[uid]) return userCache[uid];
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          userCache[uid] = data;
          return data;
        }
        return null;
      };
      try {
        // Reviews where user was a guest (hostReview)
        const hostReviewsQuery = query(
          collection(db, 'dinnerRequests'),
          where('userId', '==', host.hostId),
          where('hostReview', '!=', null)
        );
        const hostReviewsSnapshot = await getDocs(hostReviewsQuery);
        for (const docSnap of hostReviewsSnapshot.docs) {
          const data = docSnap.data();
          if (data.hostReview && data.hostReview.text) {
            const author = await getUserData(data.hostReview.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.hostReview.text,
              author: authorName
            });
          }
        }
        // Reviews where user was a host (guestReview)
        const guestReviewsQuery = query(
          collection(db, 'dinnerRequests'),
          where('hostId', '==', host.hostId),
          where('guestReview', '!=', null)
        );
        const guestReviewsSnapshot = await getDocs(guestReviewsQuery);
        for (const docSnap of guestReviewsSnapshot.docs) {
          const data = docSnap.data();
          if (data.guestReview && data.guestReview.text) {
            const author = await getUserData(data.guestReview.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.guestReview.text,
              author: authorName
            });
          }
        }
        setReviews(fetchedReviews);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    }
    fetchReviews();
  }, [host.hostId]);

  const handleProfileClick = () => {
    if (hostProfile && !isProfileLoading) {
      setShowProfilePreview(true);
    }
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setShowEnlargedImage(true);
  };

  const handleCloseEnlargedImage = () => {
    setShowEnlargedImage(false);
  };

  if (!host) return null;

  // Defensive checks for nested objects
  const dinnerDetails = host.dinnerDetails || {};
  const address = host.address || {};

  const handleClose = () => {
    onClose();
  };

  // Get country code from country name
  // Using countryToAlpha2 from 'country-to-iso' to handle all country names
  // const getCountryCode = (country) => {
  //   const countryMap = {
  //     'France': 'FR',
  //     'Germany': 'DE',
  //     'Italy': 'IT',
  //     'United States': 'US',
  //     'United States of America': 'US',
  //     'Israel': 'IL',
  //     'United Kingdom': 'GB',
  //     // Add more mappings as needed
  //   };
  //   return countryMap[country] || country;
  // };

  // Format the dates
  const formatDates = () => {
    if (dinnerDetails.recurringOption) {
      const frequencyMap = {
        'weekly': 'Every week',
        'biweekly': 'Every two weeks',
        'triweekly': 'Every three weeks',
        'monthly': 'Every month'
      };
      return `${frequencyMap[dinnerDetails.recurringOption]}`;
    }

    if (!Array.isArray(dinnerDetails.availableDates) || dinnerDetails.availableDates.length === 0) {
      return 'No dates available';
    }

    return dinnerDetails.availableDates
        .map(dateStr => {
          try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
              return '';
            }
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } catch (error) {
            console.error('Error formatting date:', error);
            return '';
          }
        })
        .filter(date => date !== '')
        .join(', ');
  };

  const locationDisplay = [
    address.neighborhood,
    address.city,
    address.country
  ].filter(Boolean).join(', ');

  // Format meal style display
  const mealStyleDisplay = Array.isArray(dinnerDetails.mealStyle)
      ? dinnerDetails.mealStyle.join(', ')
      : dinnerDetails.mealStyle || 'Not specified';

  // Format kosher type display
  const kosherTypeDisplay = dinnerDetails.kosherType || 'Not specified';

  return (
      <div className="modal-overlay" onClick={handleClose}>

        {/* Navigation Arrows - Moved outside modal-content */}
        {currentIndex > 0 && (
            <button className="nav-arrow left-arrow" onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}>&lt;</button>
        )}
        {currentIndex < dinners.length - 1 && (
            <button className="nav-arrow right-arrow" onClick={(e) => { e.stopPropagation(); onNavigate(1); }}>&gt;</button>
        )}

        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={handleClose}>×</button>

          <div className="modal-header">
            <img
                src={hostProfile?.profileImg || '/placeholder-profile.png'}
                alt={`${hostProfile?.name || 'Host'}'s picture`}
                className="host-image-large"
                onClick={handleImageClick}
                style={{ cursor: 'pointer' }}
            />
            <div>
                <h2
                    onClick={handleProfileClick}
                    style={{ 
                      cursor: (hostProfile && !isProfileLoading) ? 'pointer' : 'default'
                    }}
                    className="host-detail-profile-name"
                >
                  {hostProfile?.name}
                </h2>
                <p className="location">
                    {locationDisplay}
                    {address.country && (
                        <ReactCountryFlag
                            countryCode={countryToAlpha2(address.country)}
                            svg
                            style={{ fontSize: '1em', marginLeft: 8, marginRight: 2, verticalAlign: 'middle' }}
                        />
                    )}
                </p>
            </div>
          </div>

          <div className="modal-body">
            <div className="details-grid">
              <div className="detail-item">
                <strong>Languages spoken:</strong> {hostProfile?.languages || 'Not specified'}
              </div>
              <div className="detail-item">
                <strong>Kosher Type:</strong> {kosherTypeDisplay}
              </div>
              <div className="detail-item">
                <strong>Meal style:</strong> {mealStyleDisplay}
              </div>
              <div className="detail-item">
                <strong>Dinner Time:</strong> {dinnerDetails.time || 'Not specified'}
              </div>
              <div className="detail-item">
                <strong>Max guests:</strong> {dinnerDetails.maxGuests || 'Not specified'}
              </div>
            </div>

            <div className="available-dates host-detail-available-dates">
              <strong>Available Dates:</strong> {formatDates()}
            </div>

            <div className="host-description">
              <h3 className="host-description-title">A few words about our Shabbat dinner:</h3>
              <p>{dinnerDetails.description || 'No description provided.'}</p>
            </div>
          </div>

          <div className="modal-footer">
            <div className="button-container">
              {isRequestSent ? (
                <button className="request-button sent" disabled>
                  Request Sent
                </button>
              ) : (
                <button className="request-button" onClick={onOpenRequest}>
                  Request to join
                </button>
              )}
            </div>
          </div>

          {showProfilePreview && hostProfile && (
              <PreviewProfileModal
                  open={showProfilePreview}
                  onClose={() => setShowProfilePreview(false)}
                  profile={{
                    uid: host.hostId,
                    avatar: hostProfile.profileImg,
                    name: hostProfile.name,
                    city: hostProfile.location?.city,
                    country: hostProfile.location?.country,
                    bio: hostProfile.about,
                    languages: hostProfile.languages,
                    interests: hostProfile.interests,
                    gallery: hostProfile.gallery,
                    shabbotAnswers: host.shabbotAnswers,
                  }}
              />
          )}

          {showEnlargedImage && hostProfile?.profileImg && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.85)',
              zIndex: 40000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }} onClick={handleCloseEnlargedImage}>
              <div style={{
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
                position: 'relative'
              }}>
                <img
                  src={hostProfile.profileImg}
                  alt="Profile Large"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onClick={e => e.stopPropagation()}
                />
                <button 
                  onClick={handleCloseEnlargedImage} 
                  style={{ 
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: 'white',
                    border: '2px solid #ccc',
                    color: 'black',
                    fontSize: 24,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 40001,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
  );
}

export default HostDetailModal;